/**
 * TagAutocomplete Component
 *
 * Autocomplete input for selecting tags from existing system tags.
 * Fetches available tags from the API. Supports search and custom values.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { tagStyleFrom, useTagCatalog } from '@/shared/hooks/useTagCatalog';
import { Icon } from '@/shared/icons'
export interface TagAutocompleteProps {
  /** Current value (tag string) */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * TagAutocomplete - Dropdown for selecting existing tags with search and custom input
 */
export const TagAutocomplete: React.FC<TagAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Selecione uma tag...',
  disabled = false,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Catálogo de tags do tenant (com cor). Desde 22/09/2026 vem da collection
  // `tags`, e não mais de um `distinct` sobre os contatos — é o que permite
  // configurar um nó de workflow com uma tag que ainda não foi usada em
  // ninguém.
  const { data: catalog } = useTagCatalog();

  const allTags = useMemo(() => (catalog ?? []).map((tag) => tag.name), [catalog]);

  // Filter tags based on search term
  const filteredTags = useMemo(() => {
    if (!searchTerm.trim()) return allTags;
    const lower = searchTerm.toLowerCase();
    return allTags.filter(tag => tag.toLowerCase().includes(lower));
  }, [allTags, searchTerm]);

  // Custom value option
  const showCustomOption = useMemo(() => {
    if (!searchTerm.trim()) return false;
    return !allTags.some(t => t.toLowerCase() === searchTerm.trim().toLowerCase());
  }, [searchTerm, allTags]);

  const totalItems = filteredTags.length + (showCustomOption ? 1 : 0);

  // Update dropdown position
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 280),
      });
    }
  }, [isOpen]);

  // Reset selected index when filtered tags change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredTags.length]);

  // Scroll selected item into view
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const el = dropdownRef.current.querySelector('[data-selected="true"]');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (tag: string) => {
    onChange(tag);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, totalItems - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (showCustomOption && selectedIndex === filteredTags.length) {
          onChange(searchTerm.trim());
          setIsOpen(false);
          setSearchTerm('');
        } else if (filteredTags[selectedIndex] !== undefined) {
          handleSelect(filteredTags[selectedIndex]);
        } else if (showCustomOption) {
          onChange(searchTerm.trim());
          setIsOpen(false);
          setSearchTerm('');
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleContainerClick = () => {
    if (disabled) return;
    setIsOpen(true);
    inputRef.current?.focus();
  };

  // Highlight matching text
  const highlightMatch = (text: string) => {
    if (!searchTerm) return text;
    const lowerText = text.toLowerCase();
    const lowerSearch = searchTerm.toLowerCase();
    const index = lowerText.indexOf(lowerSearch);
    if (index === -1) return text;
    return (
      <>
        {text.slice(0, index)}
        <span className="bg-yellow-200 text-yellow-900">
          {text.slice(index, index + searchTerm.length)}
        </span>
        {text.slice(index + searchTerm.length)}
      </>
    );
  };

  // Render dropdown
  const renderDropdown = () => {
    if (!isOpen) return null;

    return createPortal(
      <div
        ref={dropdownRef}
        className="fixed z-[9999] bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto"
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
        }}
      >
        {/* Search input */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-2">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Buscar tag..."
            className="w-full h-[38px] px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            autoFocus
          />
        </div>

        {/* Tag list */}
        {filteredTags.length > 0 ? (
          <div className="py-1">
            {/* Tags header */}
            <div className="px-3 py-1.5 bg-gray-50 border-y border-gray-100">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                🏷️ Tags disponíveis
              </span>
            </div>

            {filteredTags.map((tag, idx) => {
              const isSelected = idx === selectedIndex;

              return (
                <button
                  key={tag}
                  type="button"
                  data-selected={isSelected}
                  className={cn(
                    'w-full px-3 py-2 text-left hover:bg-gray-100 transition-colors',
                    isSelected && 'bg-violet-50'
                  )}
                  onClick={() => handleSelect(tag)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <span
                    className="inline-flex items-center max-w-full px-2 py-0.5 rounded-full text-xs font-medium border truncate"
                    style={tagStyleFrom(catalog, tag)}
                  >
                    {highlightMatch(tag)}
                  </span>
                </button>
              );
            })}

            {/* Custom value option when results exist */}
            {showCustomOption && (
              <div className="border-t border-gray-100">
                <button
                  type="button"
                  data-selected={selectedIndex === filteredTags.length}
                  className={cn(
                    'w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-100 transition-colors',
                    selectedIndex === filteredTags.length && 'bg-violet-50'
                  )}
                  onClick={() => {
                    onChange(searchTerm.trim());
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  onMouseEnter={() => setSelectedIndex(filteredTags.length)}
                >
                  <span className="text-sm text-violet-600 flex-shrink-0">Usar:</span>
                  <span className="text-sm text-gray-900 font-mono truncate">
                    {searchTerm.trim()}
                  </span>
                  {/* Palavra fora do catálogo é cadastrada quando o fluxo
                      rodar (auto-cadastro). Dizer isso aqui evita a surpresa
                      de ver uma tag nova aparecer no vocabulário da empresa. */}
                  <span className="text-[10px] text-gray-400 flex-shrink-0 ml-auto">nova tag</span>
                </button>
              </div>
            )}
          </div>
        ) : showCustomOption ? (
          <div className="py-1">
            <button
              type="button"
              data-selected={selectedIndex === 0}
              className={cn(
                'w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-100 transition-colors',
                selectedIndex === 0 && 'bg-violet-50'
              )}
              onClick={() => {
                onChange(searchTerm.trim());
                setIsOpen(false);
                setSearchTerm('');
              }}
              onMouseEnter={() => setSelectedIndex(0)}
            >
              <span className="text-sm text-violet-600 flex-shrink-0">Usar:</span>
              <span className="text-sm text-gray-900 font-mono truncate">
                {searchTerm.trim()}
              </span>
            </button>
          </div>
        ) : (
          <div className="px-3 py-6 text-center text-sm text-gray-500">
            {!catalog ? 'Carregando tags...' : 'Nenhuma tag cadastrada'}
          </div>
        )}

        {/* Help footer */}
        <div className="sticky bottom-0 px-3 py-1.5 bg-gray-50 border-t text-[10px] text-gray-400 flex justify-between">
          <span>↑↓ navegar</span>
          <span>Enter selecionar</span>
          <span>Esc fechar</span>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Main input container */}
      <div
        onClick={handleContainerClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        className={cn(
          'w-full h-[38px] px-3 py-2 border rounded-lg cursor-pointer flex items-center justify-between gap-2',
          'focus:outline-none focus:ring-2 focus:ring-violet-500',
          disabled && 'cursor-not-allowed opacity-60',
          isOpen && 'ring-2 ring-violet-500'
        )}
        style={{
          backgroundColor: disabled ? 'var(--theme-button-ghost-hover)' : 'var(--theme-input-background)',
          borderColor: isOpen ? '#8b5cf6' : 'var(--theme-input-border)',
          color: 'var(--theme-input-text)',
        }}
      >
        <span
          className="text-sm truncate"
          style={{
            color: value ? 'var(--theme-card-text)' : 'var(--theme-muted-text)',
          }}
        >
          {value || placeholder}
        </span>
        <Icon name="chevron-down" className={cn(
            'w-4 h-4 flex-shrink-0 transition-transform',
            isOpen && 'rotate-180'
          )}
          style={{ color: 'var(--theme-muted-text)' }} />
      </div>

      {/* Dropdown */}
      {renderDropdown()}
    </div>
  );
};

TagAutocomplete.displayName = 'TagAutocomplete';

export default TagAutocomplete;
